import type { DownloadsByVersionDto } from '@planuze/admin-contracts';
import {
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { Download } from 'lucide-react';
import { useDownloadsContent } from '../../content/sections/downloads.js';
import { fmtDateTime, fmtNumber } from '../../lib/format.js';

interface Props {
  readonly items: DownloadsByVersionDto['items'];
}

const channelTone = {
  stable: 'success',
  beta: 'info',
  canary: 'warning',
} as const;

export function DownloadsByVersionTable({ items }: Props) {
  const copy = useDownloadsContent();
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Download className="size-6" />}
        title={copy.emptyTitle}
        description={copy.emptyDescription}
      />
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{copy.columns.version}</TableHead>
          <TableHead>{copy.columns.channel}</TableHead>
          <TableHead>{copy.columns.humanClicks}</TableHead>
          <TableHead>{copy.columns.lastClick}</TableHead>
          <TableHead>{copy.columns.activeUsers}</TableHead>
          <TableHead>{copy.columns.activeDevices}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.flatMap((item) => {
          /* Sem release local ainda há adoção legítima (versão legada/unknown). Uma linha visual
             mantém essa adoção global sem inventar canal ou clique. */
          const downloads = item.downloads.length > 0 ? item.downloads : [null];
          return downloads.map((download, index) => (
            <TableRow key={`${item.version}:${download?.channel ?? 'none'}`}>
              {index === 0 && (
                <TableCell
                  rowSpan={downloads.length}
                  className="font-mono font-medium text-foreground"
                >
                  {item.version === 'unknown' ? copy.unknownVersion : `v${item.version}`}
                </TableCell>
              )}
              <TableCell>
                {download === null ? (
                  <span className="text-muted-foreground">{copy.unknownChannel}</span>
                ) : (
                  <StatusBadge variant={channelTone[download.channel]} dot>
                    {copy.channels[download.channel]}
                  </StatusBadge>
                )}
              </TableCell>
              <TableCell numeric>{fmtNumber(download?.humanDownloadCount ?? 0)}</TableCell>
              <TableCell className="text-muted-foreground">
                {download?.lastHumanDownloadAt === null || download === null
                  ? copy.never
                  : fmtDateTime(download.lastHumanDownloadAt)}
              </TableCell>
              {index === 0 && (
                <>
                  <TableCell rowSpan={downloads.length} numeric>
                    {fmtNumber(item.activeUsers24h)}
                  </TableCell>
                  <TableCell rowSpan={downloads.length} numeric>
                    {fmtNumber(item.activeDevices24h)}
                  </TableCell>
                </>
              )}
            </TableRow>
          ));
        })}
      </TableBody>
    </Table>
  );
}
